import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import useCallbackAsync from 'hooks/useCallbackAsync';
import dataRoomApi from '../dataRoomApi';
import { DataRoomRFICategory } from '../models/DataRoomRfi';
import RfiContext from '../providers/Rfi/Rfi.context';
import DataRoomContext from '../providers/DataRoom/DataRoom.context';

const useRfiReassignCategories = (
  previousCategories: DataRoomRFICategory[],
  onCategoryUpdate: (category: DataRoomRFICategory) => void,
  onSent?: () => void,
) => {
  const { t } = useTranslation();

  const { showActionUndo } = useContext(RfiContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const categoryIds = previousCategories.map(({ _id }) => _id);

  const onUndoReassignCategories = useCallback(async () => {
    await dataRoomApi.rfi.undoReassignCategories(dataRoomId, categoryIds);

    previousCategories.forEach(onCategoryUpdate);
  }, [dataRoomId, categoryIds, previousCategories, onCategoryUpdate]);

  const [reassignCategories, loading] = useCallbackAsync(
    async (assignedTo: string) => {
      await dataRoomApi.rfi.reassignCategories(
        dataRoomId,
        categoryIds,
        assignedTo,
      );

      previousCategories.forEach((category) =>
        onCategoryUpdate({ ...category, assignedTo, reminders: [] }),
      );

      onSent?.();

      showActionUndo(
        t('documentsReassignedSuccess', {
          count: categoryIds.length,
          email: assignedTo,
        }),
        onUndoReassignCategories,
      );
    },
  );

  return {
    reassignCategories,
    loading,
  };
};

export default useRfiReassignCategories;
